import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { findValueByKeys, reduceObjectByKeys } from "@utilities/index";
import localStorageSecureWrapper from "@utilities/localStorageSecureWrapper";
import i18next from "i18next";

import type { ApiResponse, ApiValidationErrorResponse } from "./types";

export const errorsKeys = [
  "message",
  "Message",
  "errorMessage",
  "ErrorMessage",
  "Error",
  "error",
  "errors",
  "Errors",
];

export const authInterceptor = (headers: Headers) => {
  const accessToken = localStorageSecureWrapper.getItem("accessToken");
  const userId = localStorage.getItem("userId");
  const currentLang = i18next.language;

  if (accessToken) {
    headers.set("Authorization", String(accessToken));
    headers.set("userId", String(userId));
    headers.set("lang", String(currentLang));
  }
  return headers;
};

export const refreshInterceptor = (headers: Headers) => {
  const refreshToken = localStorageSecureWrapper.getItem("refreshToken");

  if (refreshToken) {
    headers.set("Authorization", String(refreshToken));
  }
  return headers;
};

export const apiErrorMessages: Record<number, string> = {
  400: "The request could not be understood by the server due to malformed syntax",
  401: "You are not authorized to perform this action",
  403: "The request was understood, but it has been refused or access is not allowed",
  404: "The requested resource could not be found",
  500: "Internal server error",
};

export const isApiValidationError = (
  error: unknown,
): error is ApiValidationErrorResponse => {
  if (typeof error === "object" && error !== null) {
    const apiError = error as ApiValidationErrorResponse;
    return (
      apiError.data?.errors !== undefined &&
      Object.keys(apiError.data.errors).length > 0
    );
  }
  return false;
};

export const getApiErrorMessage = (error: FetchBaseQueryError) => {
  if (error.status === "FETCH_ERROR")
    return "Cannot connect to the server, Check your internet network";
  if (typeof error.status !== "number") return "Unknown error occurred";

  if (isApiValidationError(error)) {
    const apiErrorsList = Object.values(error.data?.errors);
    return apiErrorsList;
  }

  if (typeof error.data === "object" && error.data !== null) {
    const response = error.data as ApiResponse<unknown>;
    const apiErrorsList = Object.values(
      reduceObjectByKeys(response, errorsKeys),
    );
    return (
      apiErrorsList ??
      findValueByKeys(response, errorsKeys) ??
      "Something went wrong, please try again later"
    );
  }

  return (
    apiErrorMessages[error.status] ||
    "Something went wrong, please try again later"
  );
};

export const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));